import React, { useEffect } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';
import TagManager from 'react-gtm-module';

import { getHasLoadedOptions, getLeadSourceOptions } from 'store/modules/options/selectors';
import { IReducerState, history } from 'store/main-reducer';
import {
  getWorkItOutData, getContactDetailsData, getCompanyDetailsData, getPayrollData, getSignUpSubmitSucceeded, getSignUpSubmitFailed, getSignUpSubmitIsLoading, getSummaryData,
} from 'store/modules/sign-up/selectors';
import {
  IWorkItOutData, IContactDetails, ICompanyDetails, IPayroll, ISummary,
} from 'types';
import { readableAddress } from 'tools/utilities/readable-address';
import { actionSetSummary, actionSubmit, TYPES } from 'store/modules/sign-up/actions';
import { PageLoader } from 'views/components/PageLoader/PageLoader';
import { getIsViewportSmall } from 'store/modules/app/selectors';
import { Modal } from 'views/components/Modal/Modal';
import { actionShowModal } from 'store/modules/app/actions';
import { SummaryView } from './Summary';
import { createContactArray } from './createContactArray';

interface Props {
  intl: IntlShape;
  defaultValues?: ISummary;
  setSummary: typeof actionSetSummary;
  submitForm: typeof actionSubmit;
  hasLoadedOptions: boolean;
  workItOut: IWorkItOutData;
  contactDetails?: IContactDetails;
  companyDetails?: ICompanyDetails;
  payroll?: IPayroll;
  submitSucceeded: boolean;
  submitFailed: string;
  isLoading: boolean;
  isViewportSmall: boolean;
  showModal: typeof actionShowModal;
  summaryData: ISummary;
  leadSource: string[];
}

const SummaryController = (props: Props) => {
  const {
    intl,
    hasLoadedOptions,
    contactDetails,
    companyDetails,
    payroll,
    workItOut,
    setSummary,
    submitForm,
    submitSucceeded,
    submitFailed,
    isLoading,
    isViewportSmall,
    showModal,
    summaryData,
    leadSource,
  } = props;

  const {
    additionalComments,
    howDidYouHearAboutUs,
    sendMeInformation,
    howDidYouHearAboutUsOther,
  } = summaryData;

  const dispatch = useDispatch();

  useEffect(() => {
    if (submitSucceeded) {
      /**
       * Notifies GTM of the form submission
       */
      TagManager.dataLayer({
        dataLayer: {
          event: 'signUpSubmitted',
        },
      });

      history.push('/sign-up/next-steps');
    }
  }, [submitSucceeded]);

  useEffect(() => {
    if (submitFailed) {
      showModal();
    }
  }, [submitFailed, showModal]);

  const saveSummaryValues = (summary: ISummary) => {
    setSummary(summary);
  };

  const goToNextStep = (summary: ISummary) => {
    // Set the summary
    setSummary(summary);
    submitForm();
  };

  const goToPreviousStep = () => {
    history.push('/sign-up/payroll-information');
  };

  const contact = contactDetails?.contact;

  if (!hasLoadedOptions || !workItOut || !contactDetails || !contact || !companyDetails || !payroll) {
    history.push('/');
    return null;
  }

  const {
    bankChoice,
    bankOther,
    // manageHr,
  } = workItOut;

  const {
    isAccountant,
    additionalContact,
    isAdditionalPrimaryContact,
  } = contactDetails;

  const contactDetailsArray = createContactArray(contact);
  const additionalContactArray = createContactArray(additionalContact);

  const {
    hasSkippedAccountantDetails,
    accountant,
    accountantName,
    accountantBusinessName,
    accountantPhoneNumber,
    accountantNotListed,
    companyName,
    irdNumber,
    industryType,
    accountantHasAccessTo,
    postalSameAsBusiness,
    shouldAddToAccountantsUmbrellaPage,
    umbrellaPageName,
    accountantWillPaySmartPayrollFees,
  } = companyDetails;

  const businessAddress = readableAddress(companyDetails.businessAddress);
  const postalAddress = postalSameAsBusiness ? businessAddress : readableAddress(companyDetails.postalAddress);

  const accountantInfo: string[] = [];

  if (accountant) {
    accountantInfo.push(accountant.name);
  }

  // Accountant is not listed so add the business name and phone number as accountant will be empty
  if (accountantNotListed && accountantBusinessName && accountantPhoneNumber) {
    accountantInfo.push(accountantBusinessName);
    accountantInfo.push(accountantPhoneNumber);
  }

  if (accountantName) {
    accountantInfo.push(accountantName);
  }

  const {
    processingPayroll,
    payePaymentsToIrd,
    employeeAnnualLeaveRollover,
    averageGrossAmountPerPayRun, // ASB only
    bankAccountName,
    bankAccountNumber,
    authoriseDirectDebitMonthlyFees,
    notAuthorisedButRequestDirectDebitAuthority,
  } = payroll;

  const payrollInformation = [`${bankChoice?.name}`];
  if (bankOther) {
    payrollInformation.push(bankOther || '');
  }
  payrollInformation.push(bankAccountName);
  payrollInformation.push(bankAccountNumber);

  return (
    <>
      {isLoading && (<PageLoader />)}

      <Modal
        modalHeader={(
          <>
            <h2>{intl.formatMessage({ id: 'ERROR_SUBMIT_FAILED_TITLE' })}</h2>
            <p>{intl.formatMessage({ id: 'ERROR_SUBMIT_FAILED_TEXT' })}</p>
          </>
        )}
        onModalClose={() => {
          // Clear the submit status once the user has closed the error modal
          dispatch({ type: TYPES.SUBMIT_CLEAR });
        }}
      />

      <SummaryView
        intl={intl}
        defaultValues={summaryData}
        saveSummaryValues={saveSummaryValues}
        goToNextStep={goToNextStep}
        goToPreviousStep={goToPreviousStep}
        isAccountant={isAccountant || false}
        contactDetails={contactDetailsArray}
        additionalContactDetails={additionalContactArray}
        isAdditionalPrimaryContact={isAdditionalPrimaryContact}
        accountantInfo={accountantInfo}
        accountantWillPaySmartPayrollFees={!!accountantWillPaySmartPayrollFees}
        shouldAddToAccountantsUmbrellaPage={shouldAddToAccountantsUmbrellaPage}
        hasSkippedAccountantDetails={hasSkippedAccountantDetails}
        umbrellaPageName={umbrellaPageName}
        companyName={companyName}
        irdNumber={irdNumber}
        industryType={industryType}
        accountantHasAccessTo={accountantHasAccessTo}
        businessAddress={businessAddress}
        postalAddress={postalAddress}
        processingPayroll={processingPayroll}
        payePaymentsToIrd={payePaymentsToIrd}
        employeeAnnualLeaveRollover={employeeAnnualLeaveRollover}
        averageGrossAmountPerPayRun={averageGrossAmountPerPayRun}
        authoriseDirectDebitMonthlyFees={intl.formatMessage({ id: authoriseDirectDebitMonthlyFees ? 'YES' : 'NO' })}
        notAuthorisedButRequestDirectDebitAuthority={notAuthorisedButRequestDirectDebitAuthority}
        payrollInformation={payrollInformation}
        isViewportSmall={isViewportSmall}
        additionalComments={additionalComments}
        howDidYouHearAboutUs={howDidYouHearAboutUs}
        sendMeInformation={sendMeInformation}
        howDidYouHearAboutUsOther={howDidYouHearAboutUsOther}
        leadSource={leadSource}
      />
    </>
  );
};

const mapStateToProps = (state: IReducerState) => ({
  hasLoadedOptions: getHasLoadedOptions(state),
  workItOut: getWorkItOutData(state),
  contactDetails: getContactDetailsData(state),
  companyDetails: getCompanyDetailsData(state),
  payroll: getPayrollData(state),
  isLoading: getSignUpSubmitIsLoading(state),
  submitSucceeded: getSignUpSubmitSucceeded(state),
  submitFailed: getSignUpSubmitFailed(state),
  isViewportSmall: getIsViewportSmall(state),
  summaryData: getSummaryData(state),
  leadSource: getLeadSourceOptions(state),
});

const mapDispatchToProps = {
  setSummary: actionSetSummary,
  submitForm: actionSubmit,
  showModal: actionShowModal,
};

export const Summary = injectIntl(connect(
  mapStateToProps,
  mapDispatchToProps,
)(SummaryController));
